
import { Vue } from "vue-class-component";
import { createGitBook } from "@gitbook/embed";
import { HelpCenterController } from "@/app/ui/controllers/HelpCenterController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { buildHelpCenterEmbedConfigure } from "@/app/infrastructures/misc/buildHelpCenterSrc";
import type { HelpCenterEmbedAction } from "@/feature-flags/flags-help-center";

const NAVIGATE_SAFETY_TIMEOUT_MS = 10000;

export default class HelpCenterPage extends Vue {
  private frame: ReturnType<
    ReturnType<typeof createGitBook>["createFrame"]
  > | null = null;
  private frameConfigured = false;
  private navigateTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private unsubscribeNavigate: (() => void) | null = null;

  get isError(): boolean {
    return HelpCenterController.isError;
  }

  get errorMessage(): string {
    return HelpCenterController.errorMessage;
  }

  get iframeSrc(): string {
    return HelpCenterController.iframeSrc;
  }

  async mounted(): Promise<void> {
    await HelpCenterController.ensureVisitorToken();
  }

  beforeUnmount(): void {
    HelpCenterController.clearRefreshTimer();
    if (HelpCenterController.showLoading) {
      MainAppController.closeLoading();
    }
    this.clearNavigateTimeout();
    this.disposeFrame();
  }

  disposeFrame(): void {
    if (this.unsubscribeNavigate) {
      this.unsubscribeNavigate();
      this.unsubscribeNavigate = null;
    }
    this.frame = null;
    this.frameConfigured = false;
  }

  async onRetry(): Promise<void> {
    this.clearNavigateTimeout();
    this.disposeFrame();
    await HelpCenterController.ensureVisitorToken(true);
  }

  onActionClick = (action: HelpCenterEmbedAction): void => {
    HelpCenterController.startActionLoading();
    this.clearNavigateTimeout();
    this.navigateTimeoutId = setTimeout(() => {
      HelpCenterController.onIframeLoaded();
      this.navigateTimeoutId = null;
    }, NAVIGATE_SAFETY_TIMEOUT_MS);
  };

  onFrameNavigate = (): void => {
    HelpCenterController.onIframeLoaded();
    this.clearNavigateTimeout();
  };

  clearNavigateTimeout(): void {
    if (this.navigateTimeoutId !== null) {
      clearTimeout(this.navigateTimeoutId);
      this.navigateTimeoutId = null;
    }
  }

  onIframeLoaded(): void {
    HelpCenterController.onIframeLoaded();
    if (this.frameConfigured) return;

    const iframeEl = this.$refs.iframeEl as HTMLIFrameElement | undefined;
    const siteUrl = HelpCenterController.siteUrl;
    if (!iframeEl || !siteUrl) return;

    const gitbook = createGitBook({ siteURL: siteUrl });
    this.frame = gitbook.createFrame(iframeEl);
    this.frame.configure(
      buildHelpCenterEmbedConfigure(this.frame, this.onActionClick)
    );
    this.unsubscribeNavigate = this.frame.on("navigate", this.onFrameNavigate);
    this.frameConfigured = true;
  }
}
